import React, { Fragment } from "react";
import { useIntl } from 'react-intl';
import Helmet from "react-helmet";

import styles from "./aboutUsTemplate.module.scss";
import SEO from "@components/seo";
import UniplacesFullContainer from "@components/UniplacesFullContainer/uniplacesFullContainer";
import HeroSection from "@components/HeroSection/heroSection";
import TitleSection from "@components/aboutUsPage/TitleSection/titleSection";
import TextSection from "@components/aboutUsPage/TextSection/textSection";
import Accordion from "@components/Accordion/accordion";
import Separator from "@components/aboutUsPage/Separator/separator";
import { renderHowDoesUniplacesWorkText } from "@utils/translations";

const ABOUT_US_FAQ_SECTION_KEYS = [
  'Is Uniplaces safe?',
  'Is Uniplaces legit or a scam?',
  'How does Uniplaces work?',
  'Are listings verified on Uniplaces?',
  'Are payments secure?',
  'What Is the cancellation policy on Uniplaces?',
  'Can I rent accommodation before arriving?',
  'What happens if the apartment is not as described?',
  'What happens if the landlord cancels a booking?',
  'Why many international tenants use Uniplaces?'
];

const AboutUsTemplate = ({ pageContext: { locale } }) => {
  const intl = useIntl();

  const heroHeading = intl.formatMessage({ id: 'About Us' });
  const heroSubHeading = intl.formatMessage({ id: 'About Us text' });
  const heroImage = {
    url: `${process.env.CDN_URL}/images/about-us.jpg`,
    alt: 'About Us Hero Image'
  };

  const trustAndSafetyTitle = intl.formatMessage({ id: 'Trust & Safety on Uniplaces' });
  const trustAndSafetyText = intl.formatMessage({ id: 'Trust & Safety on Uniplaces text' });

  const accordionItems = ABOUT_US_FAQ_SECTION_KEYS.map((sectionId, index) => {
    const isHowDoesUniplacesWorkSection = index === 2;
    const text = intl.formatMessage({ id: `${sectionId} text` });

    return { 
      title: intl.formatMessage({ id: sectionId }),
      text: isHowDoesUniplacesWorkSection ? renderHowDoesUniplacesWorkText(text) : text,
      shouldRenderWithBreaks: !isHowDoesUniplacesWorkSection
    };
  });

  const exploreAccommodationTitle = intl.formatMessage({ id: 'Explore Accommodation in Our Cities' });
  const exploreAccommodationText = intl.formatMessage({ id: 'Explore Accommodation in Our Cities text' });

  const faqSchemaItems = ABOUT_US_FAQ_SECTION_KEYS.map((sectionId) => ({
    "@type": "Question",
    "name": intl.formatMessage({ id: sectionId }),
    "acceptedAnswer": {
      "@type": "Answer",
      "text": intl.formatMessage({ id: `${sectionId} FAQ text` })
    }
  }));

  const faqSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqSchemaItems
  });

  return (
    <Fragment>
      <SEO
        titleKey="About us"
        descriptionKey="Rent your accommodation with Uniplaces, the safe and easy housing platform to find and book rooms, apartments, flats and residences."
        imageUrl={heroImage.url}
        lang={locale}
      />
      <HeroSection
        heroHeading={heroHeading}
        heroSubheading={heroSubHeading}
        heroImage={heroImage}
        className={styles.aboutUsHeroSection}
      />
      <UniplacesFullContainer containerClassName={styles.aboutUsUniplacesContainer}>
          <TitleSection title={trustAndSafetyTitle} text={trustAndSafetyText} />
          <Separator />
          <Accordion items={accordionItems} className={styles.aboutUsAccordion} />
          <Separator />
          <TextSection title={exploreAccommodationTitle} text={exploreAccommodationText} />
      </UniplacesFullContainer>
      <Helmet>
        <script key="about-us" type="application/ld+json">{faqSchema}</script>
      </Helmet>
    </Fragment>
  );
};

export default AboutUsTemplate;
